import { useRouterState } from '@tanstack/react-router';
import type { i18n as I18n } from 'i18next';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { notFoundHead, type PageHead, pageHead, type PageHeadSources } from './page-head';
import { buildPageInventory, type PageInventoryEntry } from './page-inventory';
import catalogJson from '~/data/catalog.json';
// Not ~/entities/blog/api, which this main-chunk module only loads on demand: its eager `?meta` glob would bring
// every article's meta into the main chunk.
import { getPostLanguages, getPostSlugs } from '~/entities/blog/languages';
import { loadSeoMeta } from '~/entities/catalog/seo-meta';
import type { Catalog } from '~/entities/catalog/types';
import { type LanguageCode, languageCodes } from '~/texts/languages';

/** The page the router has finished loading: its internal path, the search page's query, and whether it is a 404. */
type ResolvedPage = { internalPath: string; searchQuery?: string; notFound: boolean };

/**
 * The pages of a language as pageInventory(lang) lists them, with the blog articles of the blog languages index:
 * pageInventory's own glob of src/content/blog would add a loader per article and language (~40 kB) to the main
 * chunk just for the file names.
 */
export function clientPageInventory(lang: LanguageCode): PageInventoryEntry[] {
  const blog = Object.fromEntries(getPostSlugs().map((slug) => [slug, getPostLanguages(slug)]));

  return buildPageInventory(lang, { catalog: catalogJson as unknown as Catalog, blog });
}

const inventories = new Map<LanguageCode, Map<string, PageInventoryEntry>>();

/** The inventory entry of an internal path in a language; each language's index is built on first use. */
function inventoryEntry(lang: LanguageCode, internalPath: string): PageInventoryEntry | undefined {
  let entries = inventories.get(lang);
  if (!entries) {
    entries = new Map(clientPageInventory(lang).map((entry) => [entry.internalPath, entry]));
    inventories.set(lang, entries);
  }

  return entries.get(internalPath);
}

/** `/categories/x/` and `/categories/x` are the same page; the inventory lists the form without the slash. */
const withoutTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

/**
 * The texts of the active i18next language only, raw as in public/translations/<lang>.json (no interpolation,
 * no fallback to the start language that LanguageProvider makes i18next's fallbackLng), like the server's
 * `translateFrom(table)`.
 */
function translateFromI18n(i18n: I18n, lang: LanguageCode): PageHeadSources['translate'] {
  return (key) => {
    const text: unknown = i18n.getResource(lang, 'translation', key);

    return typeof text === 'string' && text ? text : undefined;
  };
}

/** Sets `<meta name="description">`, creating the tag on first use; undefined removes it. */
function setMetaDescription(content: string | undefined): void {
  let tag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (content === undefined) {
    tag?.remove();

    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'description';
    document.head.appendChild(tag);
  }
  tag.content = content;
}

/** Puts a page's title and description into the document head; a page without a description has the tag removed. */
export function applyDocumentHead({ title, description }: Pick<PageHead, 'title' | 'description'>): void {
  document.title = title;
  setMetaDescription(description);
}

/** The head of the resolved page, or undefined when the inventory and the head data disagree (pageHead throws). */
function resolvedPageHead(lang: LanguageCode, page: ResolvedPage, sources: PageHeadSources): PageHead | undefined {
  const entry = page.notFound ? undefined : inventoryEntry(lang, page.internalPath);
  if (!entry) {
    return notFoundHead(lang, sources);
  }
  try {
    return pageHead(lang, entry, sources, { searchQuery: page.searchQuery });
  } catch {
    return undefined;
  }
}

/**
 * Keeps document.title and `<meta name="description">` on the page shown after each client navigation, with the
 * same pageHead() the server renders the first load's head with (src/entities/seo/page-head.ts): the inventory
 * entry of the current internal path in the UI language, the not-found head when the router shows not-found (a
 * product, category, subcategory or blog route throws notFound() for an unknown id or slug) or the path is no page
 * of the inventory.
 *
 * Canonical, hreflang, Open Graph and JSON-LD are deliberately not updated here: crawlers and link previews only
 * read the head the server sends with each address, so in the browser they would be work without a reader.
 *
 * Called once, by the root route component.
 */
export function useDocumentHead(): void {
  const { i18n } = useTranslation();
  const page = useRouterState({
    // Only a finished navigation: while one loads (or redirects), the location is ahead of the page shown.
    select: (state): ResolvedPage | null => {
      const location = state.resolvedLocation;
      if (state.status !== 'idle' || !location) {
        return null;
      }
      const internalPath = withoutTrailingSlash(location.pathname);
      const query: unknown = internalPath === '/search' ? location.search.search : undefined;

      return {
        internalPath,
        ...(typeof query === 'string' || typeof query === 'number' ? { searchQuery: String(query) } : {}),
        notFound: state.matches.some((match) => match.status === 'notFound'),
      };
    },
    structuralSharing: true,
  });
  // Re-rendered by react-i18next once the next language's texts have loaded.
  const language = i18n.isInitialized ? i18n.language : undefined;
  const lang = languageCodes.find((code) => code === language);

  useEffect(() => {
    if (!page || !lang) {
      return undefined;
    }
    // The language's SEO table loads once, then resolves from cache; a navigation in the meantime cancels this
    // one, so a late table never puts the previous page's head over the current one.
    let cancelled = false;
    // An article's head needs its meta: the blog API is loaded for article pages only (their page component has
    // loaded it already), so it stays out of the main chunk.
    const blogApi = page.internalPath.startsWith('/blog/') ? import('~/entities/blog/api') : Promise.resolve(undefined);
    void Promise.all([loadSeoMeta(lang), blogApi]).then(([seoMeta, blog]) => {
      if (cancelled) {
        return;
      }
      const head = resolvedPageHead(lang, page, {
        translate: translateFromI18n(i18n, lang),
        seoMeta,
        blogMeta: (slug) => blog?.getPostMeta(slug, lang),
      });
      if (head) {
        applyDocumentHead(head);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [page, lang, i18n]);
}
